import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, Play, Pause, RotateCcw, ArrowRight, Activity, GitBranch, Copy } from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/clipboard";

const TURN_DELAY_MS = 1500;

export default function Replay() {
  const { sid } = useParams();
  const [doc, setDoc] = useState(null);
  const [shownCount, setShownCount] = useState(1); // welcome msg shown at start
  const [playing, setPlaying] = useState(true);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get(`/ai/chat/${sid}?replay=1`)
      .then((r) => {
        if (!r.data?.messages || r.data.messages.length === 0) {
          setError("This replay link has no conversation yet.");
        }
        setDoc(r.data);
      })
      .catch(() => setError("Replay not found."));
  }, [sid]);

  useEffect(() => {
    if (!playing || !doc?.messages) return;
    if (shownCount >= doc.messages.length) return;
    timerRef.current = setTimeout(() => setShownCount((c) => c + 1), TURN_DELAY_MS);
    return () => clearTimeout(timerRef.current);
  }, [playing, shownCount, doc]);

  const restart = () => { setShownCount(0); setPlaying(true); };
  const togglePlay = () => setPlaying((p) => !p);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const copyShare = () => copyToClipboard(shareUrl, "Replay link copied");

  const q = doc?.qualification;
  const messages = (doc?.messages || []).slice(0, shownCount);
  const total = doc?.messages?.length || 0;
  const finished = total > 0 && shownCount >= total;

  return (
    <div className="min-h-screen bg-slate-50/60">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-slate-900 text-white">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 7h7M4 12h12M4 17h7" strokeLinecap="round" /><circle cx="18" cy="7" r="2.2" fill="#0055FF" stroke="none" /><circle cx="18" cy="17" r="2.2" fill="#0055FF" stroke="none" /></svg>
            </span>
            <span className="text-[15px] font-semibold tracking-tight text-slate-900">Connexis<span className="text-primary">.</span></span>
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={copyShare} className="rounded-full" data-testid="replay-share"><Copy className="size-4" /> Share replay</Button>
            <Link to="/ai-systems"><Button size="sm" className="rounded-full" data-testid="replay-try-yourself">Try the AI yourself <ArrowRight className="size-4" /></Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center gap-2 text-primary">
            <Sparkles className="size-4" />
            <span className="text-[11px] font-semibold tracking-[0.2em] uppercase">AI lead replay</span>
          </div>
          <h1 className="mt-3 text-4xl sm:text-5xl tracking-[-0.03em] font-semibold text-slate-900 leading-tight">A real AI-qualified enquiry, replayed.</h1>
          <p className="mt-3 text-slate-600 max-w-2xl">Watch our Connexis AI agent qualify a financial-services prospect turn-by-turn — and see exactly how the qualified lead would route to live buyers.</p>
        </div>
      </section>

      {error ? (
        <div className="mx-auto max-w-6xl px-6 py-24 text-center">
          <h2 className="text-2xl font-semibold text-slate-900">{error}</h2>
          <Link to="/ai-systems" className="mt-4 inline-block"><Button className="rounded-full px-5">Try the AI live <ArrowRight className="size-4" /></Button></Link>
        </div>
      ) : !doc ? (
        <div className="min-h-[40vh] grid place-items-center text-sm text-slate-500">Loading replay…</div>
      ) : (
        <section className="py-12">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-6">
            {/* Chat replay */}
            <div className="lg:col-span-3 rounded-2xl border border-slate-200 bg-white shadow-soft overflow-hidden" data-testid="replay-chat">
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
                <div className="flex items-center gap-2.5">
                  <span className="inline-grid place-items-center size-8 rounded-md bg-primary/10 text-primary"><Sparkles className="size-4" /></span>
                  <div className="leading-tight">
                    <div className="text-sm font-medium text-slate-900">Connexis AI · replay</div>
                    <div className="text-[11px] text-slate-500">Captured · {new Date(doc.created_at || Date.now()).toLocaleString("en-GB")}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" onClick={togglePlay} aria-label={playing ? "Pause" : "Play"} data-testid="replay-toggle">{playing ? <Pause className="size-4" /> : <Play className="size-4" />}</Button>
                  <Button variant="ghost" size="icon" onClick={restart} aria-label="Restart" data-testid="replay-restart"><RotateCcw className="size-4" /></Button>
                </div>
              </div>

              {/* Progress */}
              <div className="px-5 pt-3">
                <div className="h-1 w-full rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-primary transition-all duration-500" style={{ width: `${total ? (shownCount / total) * 100 : 0}%` }} />
                </div>
                <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
                  <span>Turn {shownCount} of {total}</span>
                  <span>{playing ? "Playing" : "Paused"}</span>
                </div>
              </div>

              <div className="px-5 py-4 space-y-3 max-h-[520px] overflow-y-auto">
                {messages.map((m, i) => (
                  <div key={i} className={`flex gap-2.5 animate-fade-up ${m.role === "user" ? "justify-end" : ""}`}>
                    {m.role !== "user" && <span className="shrink-0 mt-0.5 inline-flex h-6 w-12 items-center justify-center rounded-md text-[10px] font-semibold bg-primary/10 text-primary">AI</span>}
                    <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                      {m.text.split("\n").map((line, k) => <p key={k} className={k > 0 ? "mt-2" : ""}>{line}</p>)}
                    </div>
                    {m.role === "user" && <span className="shrink-0 mt-0.5 inline-flex h-6 w-12 items-center justify-center rounded-md text-[10px] font-semibold bg-slate-100 text-slate-600">YOU</span>}
                  </div>
                ))}
                {playing && shownCount < total && (
                  <div className="flex gap-2.5">
                    <span className="shrink-0 mt-0.5 inline-flex h-6 w-12 items-center justify-center rounded-md text-[10px] font-semibold bg-primary/10 text-primary">AI</span>
                    <div className="rounded-2xl px-3.5 py-2.5 bg-white border border-slate-200 inline-flex items-center gap-1.5">
                      <Dot delay={0} /><Dot delay={150} /><Dot delay={300} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Routing result panel */}
            <div className="lg:col-span-2 space-y-5">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-soft">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Routing result</div>
                  {q ? (
                    <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-700"><span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" /> {finished ? "Routed" : "Routing…"}</span>
                  ) : (
                    <span className="text-[11px] text-slate-500">Awaiting qualification</span>
                  )}
                </div>

                {q ? (
                  <div className={`mt-4 transition-opacity duration-700 ${finished ? "opacity-100" : "opacity-50"}`}>
                    <Row k="Sector" v={q.sector_label || q.sector} />
                    <Row k="Location" v={q.location || "—"} />
                    <Row k="Confidence" v={<span className="font-mono">{q.confidence}%</span>} />
                    <Row k="Estimated value" v={<span className="font-mono">£{Number(q.estimated_value || 0).toFixed(2)}</span>} />
                    <Row k="Winning buyer" v={<span className="text-primary font-medium">{q.routing?.winning_buyer}</span>} />
                    <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-slate-500"><Activity className="size-3" /> {q.routing?.latency_ms}ms · <GitBranch className="size-3" /> {q.routing?.ping_tree}</div>
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-slate-600">This conversation hasn't been qualified yet.</p>
                )}
              </div>

              {/* CTA card */}
              <div className="rounded-2xl bg-slate-900 text-white p-6 relative overflow-hidden">
                <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.4) 1px, transparent 0)", backgroundSize: "20px 20px" }} />
                <div className="relative">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold">For operators</div>
                  <h3 className="mt-2 text-2xl font-medium tracking-tight">See what your version looks like.</h3>
                  <p className="mt-1.5 text-sm text-slate-300">Spin up the same agent on your sector — your script, your buyers, your routing.</p>
                  <div className="mt-5 flex gap-2 flex-wrap">
                    <Link to="/ai-systems"><Button className="rounded-full bg-white text-slate-900 hover:bg-slate-100" data-testid="replay-cta-try">Try the AI live <ArrowRight className="size-4" /></Button></Link>
                    <Link to="/book-demo"><Button variant="outline" className="rounded-full border-white/30 text-white hover:bg-white/10" data-testid="replay-cta-call">Book a strategy call</Button></Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
      <span className="text-xs uppercase tracking-widest text-slate-500">{k}</span>
      <span className="text-sm font-medium text-slate-900">{v}</span>
    </div>
  );
}

function Dot({ delay }) {
  return <span className="block size-1.5 rounded-full bg-slate-400" style={{ animation: `pulse 1s ease-in-out ${delay}ms infinite` }} />;
}
